import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { UpdatePriceSeriesPayload } from './api';
import type { ListPriceSeriesParams } from './schema';

const ROOT_KEY = 'priceSeries';
const listKey = (params: ListPriceSeriesParams) =>
  [ROOT_KEY, 'list', params] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function usePriceSeries(params: ListPriceSeriesParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listPriceSeries(params),
    placeholderData: (prev) => prev,
  });
}

export function useUpdatePriceSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdatePriceSeriesPayload;
    }) => api.updatePriceSeries(id, payload),
    onSuccess: () => {
      // Invalidate every cached list params combo — the row's `updated_at`
      // bumps on every save, which can change the default sort position.
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      toast.success('Price series updated');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to update price series')),
  });
}
